import { Injectable } from '@angular/core';
import { CapabilityType } from '../models/capability-type';
import { SystemRole } from '../models/roles';
import { ShareType } from '../models/share-type';
import { PlanogramService } from './planogram.service';
import { RolesService } from './roles.service';

@Injectable({
  providedIn: 'root',
})
export class CapabilitiesService {
  constructor(
    private planogramService: PlanogramService,
    private rolesService: RolesService
  ) {}
  /**
   * Helper method to check if the user has a specific capability based on roles and shareType.
   * Use in component functions (e.g., to enable/disable buttons).
   * Reactive: Evaluates current roles and shareType.
   */
  hasCapability(capability: CapabilityType): boolean {
    const roles = this.rolesService?.roles.value || []; // Assuming RolesService is injected (add if not already)
    const share = this.planogramService.getShare(); // From PlanogramService
    const isExternal = roles.includes(SystemRole.EXTERNAL);
    let capabilities: CapabilityType[] = [];

    // Linked (reference) planograms are view-only for everyone, regardless of role/share: allow only
    // viewing and export (export resolves the master's layout stamped with this linked store's
    // identity). This single check locks the entire toolbar and in-canvas editing at once.
    if (this.planogramService.isLinkedPlanogram()) {
      return capability === CapabilityType.VIEW_PLANOGRAM || capability === CapabilityType.EXPORT;
    }

    // If external user, base on shareType only
    if (isExternal) {
      switch (share?.type) {
        case ShareType.VIEW:
            capabilities = [
                CapabilityType.VIEW_PLANOGRAM,
                CapabilityType.FEEDBACK
            ];
            break;
        case ShareType.EDIT:
            capabilities = [
                CapabilityType.VIEW_PLANOGRAM,
                CapabilityType.EDIT_PLANOGRAM,
                CapabilityType.SAVE_PLANOGRAM,
                CapabilityType.RESET_PLANOGRAM,
                CapabilityType.FEEDBACK
            ];
            break;
      }
        return capabilities.some((p) => p === capability);
    }

    // Internal users: Base on roles (shareType ignored, but you could modify to intersect if needed)
    if (roles.includes(SystemRole.APPROVER)) {
      capabilities = [
        CapabilityType.SHARING_CENTER,
        CapabilityType.CREATE_FOLDER,
        CapabilityType.CREATE_PLANOGRAM,
        CapabilityType.GENERATE_PLANOGRAM,
        CapabilityType.APPROVE_PLANOGRAM,
        CapabilityType.EDIT_PLANOGRAM,
        CapabilityType.VIEW_PLANOGRAM,
        CapabilityType.SAVE_PLANOGRAM,
        CapabilityType.SAVENEW_PLANOGRAM,
        CapabilityType.LOAD_PLANOGRAM,
        CapabilityType.MOVE_PLANOGRAM,
        CapabilityType.CLONE_PLANOGRAM,
        CapabilityType.REFRESH_PLANOGRAM,
        CapabilityType.DELETE_PLANOGRAM,
        CapabilityType.IMPORT_PRODUCTS,
        CapabilityType.REMOVE_PRODUCTS,
        CapabilityType.SHARE_PLANOGRAM,
        CapabilityType.EXPORT,
        CapabilityType.EDIT_OPTIONS,
        CapabilityType.EDIT_NOTES,
        CapabilityType.IMAGES_EDITOR,
        CapabilityType.FEEDBACK
      ];
    }
    else if (roles.includes(SystemRole.EDITOR)) {
      capabilities = [
        CapabilityType.SHARING_CENTER,
        CapabilityType.CREATE_FOLDER,
        CapabilityType.CREATE_PLANOGRAM,
        CapabilityType.GENERATE_PLANOGRAM,
        CapabilityType.EDIT_PLANOGRAM,
        CapabilityType.VIEW_PLANOGRAM,
        CapabilityType.SAVE_PLANOGRAM,
        CapabilityType.SAVENEW_PLANOGRAM,
        CapabilityType.LOAD_PLANOGRAM,
        CapabilityType.MOVE_PLANOGRAM,
        CapabilityType.CLONE_PLANOGRAM,
        CapabilityType.REFRESH_PLANOGRAM,
        CapabilityType.DELETE_PLANOGRAM,
        CapabilityType.IMPORT_PRODUCTS,
        CapabilityType.REMOVE_PRODUCTS,
        CapabilityType.SHARE_PLANOGRAM,
        CapabilityType.EXPORT,
        CapabilityType.EDIT_OPTIONS,
        CapabilityType.IMAGES_EDITOR,
        CapabilityType.EDIT_NOTES,
        CapabilityType.FEEDBACK
      ];
    }
    else if (roles.includes(SystemRole.VIEWER)) {
      capabilities = [
        CapabilityType.VIEW_PLANOGRAM,
        CapabilityType.LOAD_PLANOGRAM,
        CapabilityType.EXPORT,
        CapabilityType.FEEDBACK
      ];
    }

    // No matching role: Restrictive (e.g., no capabilities)
    return capabilities.some((p) => p === capability);
  }
}
